import { RankPermissions } from '@game/enums';

type BuildArray<Current extends number, Accumulator extends number[] = []> = Accumulator['length'] extends Current
  ? Accumulator
  : BuildArray<Current, [...Accumulator, 0]>;
type PlusOne<Current extends number> = [...BuildArray<Current>, 0]['length'];

export type NumberRange<Start extends number, End extends number, Result extends number = Start> = Start extends End
  ? Result | End
  : NumberRange<PlusOne<Start> & number, End, Result | Start>;

const DEFAULT_AREA = 'The Waking Ship';
const DEFAULT_CORPORATION_MEMBERSHIP_LIMIT = 5;
const MAX_CORPORATION_SIZE = 20;
const MAX_EQUIPPED_SKILLS = 8;
const MAX_PARTY_SIZE = 3;
const MAX_PROFILE_BIOGRAPHY_LENGTH = 500;
const UNNAMED = '{unnamed}';

export {
  DEFAULT_AREA,
  DEFAULT_CORPORATION_MEMBERSHIP_LIMIT,
  MAX_CORPORATION_SIZE,
  MAX_EQUIPPED_SKILLS,
  MAX_PARTY_SIZE,
  MAX_PROFILE_BIOGRAPHY_LENGTH,
  UNNAMED,
};

export const baseMaxEnergy = 200;
export const energyPerLevel = 4;
export const energyIncrement = 10;
export const energyMinuteInterval = 5;
export const baseMaxSpirit = 100;
export const spiritPerLevel = 2;
export const spiritIncrement = 5;
export const spiritMinuteInterval = 2;
export const baseMaxHealth = 100;
export const healthPerLevel = 10;

export const SECOND_MS = 1000;
export const MINUTE_MS = SECOND_MS * 60;
export const HOUR_MS = MINUTE_MS * 60;
export const DAY_MS = HOUR_MS * 24;

export const MAX_TURN_TIME_SECONDS = 30 * SECOND_MS;

export const AUTH_PROVIDERS = ['twitch', 'discord'];

export const MIN_QUALITY = 0;
export const MAX_QUALITY = 4;
export type Quality = NumberRange<typeof MIN_QUALITY, typeof MAX_QUALITY>;

export const API_KEY_HEADER = 'x-api-key';

export const globalChatChannelNames = ['main', 'trade'];

export const CORPORATION_LEADER_RANK_PRIORITY = 0;
export const CORPORATION_JOIN_REQUEST_COOLDOWN = 2 * MINUTE_MS;
export const CORPORATION_MAX_PRIVATE_DESCRIPTION_LENGTH = 250;
export const CORPORATION_MAX_PUBLIC_DESCRIPTION_LENGTH = 250;
export const CORPORATION_MAX_RANK_NAME_LENGTH = 15;
export const urls = {
  cdnAudio: 'https://cdn.betweenworlds.net/audio/',
};

export const roles = ['user', 'admin', 'moderator', 'supporter', 'editor'] as const;

export const sortKeys = ['type', 'name', 'quantity', 'quality'] as const;

export const panelIds = {
  inventory: 'inventory',
  missions: 'missions',
  equipment: 'equipment',
  chat: 'chat',
  locations: 'locations',
  party: 'party',
  moderator: 'moderator',
} as const;

export const bugReportStatuses = ['new', 'closed-fixed', 'more-info', 'in-progress', 'closed-nofix', 'closed-duplicate'] as const;

export const bugReportStatusesEnum = {
  New: 'new',
  ClosedFixed: 'closed-fixed',
  MoreInfo: 'more-info',
  InProgress: 'in-progress',
  ClosedNoFix: 'closed-nofix',
  ClosedDuplicate: 'closed-duplicate',
} as const;

export const orderedCorporationPermissions: RankPermissions[] = [
  RankPermissions.SetPublicDescription,
  RankPermissions.SetPrivateDescription,
  RankPermissions.ChangeRankPermissions,
  RankPermissions.ChangeRankPriority,
  RankPermissions.ChangeRankName,
  RankPermissions.CreateRank,
  RankPermissions.RemoveRank,
  RankPermissions.RenameRank,
  RankPermissions.SetMemberRank,
  RankPermissions.SetNewMembersRank,
  RankPermissions.SpendInfluence,
  RankPermissions.ManageMissions,
  RankPermissions.RemoveMembers,
  RankPermissions.RejectJoinRequests,
  RankPermissions.AcceptJoinRequests,
  RankPermissions.DistributeItem,
  RankPermissions.DonateItem,
];

export const MAX_CHAT_MESSAGES_PER_CHANNEL = 100_000;

export const MARKET_PAGE_SIZE = 20;
